import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileImage, Type, FileText, Sparkles, Download, Copy, RefreshCw,
  Palette, MessageSquare, Image, Clock, User, Loader2, ChevronRight,
  Megaphone, Instagram, Facebook, Mail, Printer, CheckCircle, X,
  Heart, UserPlus, Lightbulb, Gift, Share2, Twitter, Linkedin, Send, MessageCircle
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tool types
const TOOLS = [
  { 
    id: 'flyer', 
    name: 'Flyer Creator', 
    icon: FileImage, 
    description: 'Create eye-catching flyers for events',
    color: 'bg-purple/10 text-purple'
  },
  { 
    id: 'post', 
    name: 'Social Post', 
    icon: Instagram, 
    description: 'Generate social media content',
    color: 'bg-blue-100 text-blue-600'
  },
  { 
    id: 'letterhead', 
    name: 'Letterhead', 
    icon: FileText, 
    description: 'Official letters for volunteer hours',
    color: 'bg-green-100 text-green-600'
  },
  { 
    id: 'welcome', 
    name: 'Welcome Note', 
    icon: UserPlus, 
    description: 'Welcome new donors & members',
    color: 'bg-amber-100 text-amber-600'
  },
  { 
    id: 'thankyou', 
    name: 'Thank You Note', 
    icon: Heart, 
    description: 'Thank donors for their gifts',
    color: 'bg-rose-100 text-rose-600'
  }
];

// Quick templates
const QUICK_TEMPLATES = {
  flyer: [
    { name: 'Food Pantry Event', prompt: 'Food pantry distribution event with free groceries for families' },
    { name: 'Sunday Service', prompt: 'Weekly Sunday worship service invitation' },
    { name: 'Community Gathering', prompt: 'Community fellowship event with food and activities' },
    { name: 'Volunteer Call', prompt: 'Recruiting volunteers to help with ministry programs' },
  ],
  post: [
    { name: 'Weekly Inspiration', prompt: 'Inspirational message with a Bible verse' },
    { name: 'Event Reminder', prompt: 'Reminder for upcoming ministry event' },
    { name: 'Thank You', prompt: 'Thanking volunteers and donors for their support' },
    { name: 'Impact Story', prompt: 'Sharing how the ministry helped someone in need' },
  ],
  letterhead: [
    { name: 'Volunteer Hours', prompt: 'Confirmation of volunteer service hours' },
    { name: 'Donation Receipt', prompt: 'Thank you letter for donation' },
    { name: 'Reference Letter', prompt: 'Character reference for a volunteer' },
  ],
  welcome: [
    { name: 'New Donor', prompt: 'Welcome a first-time donor to our ministry family' },
    { name: 'New Member', prompt: 'Welcome a new church member who just joined' },
    { name: 'New Volunteer', prompt: 'Welcome someone who signed up to volunteer' },
    { name: 'Event Visitor', prompt: 'Welcome someone who attended an event for the first time' },
  ],
  thankyou: [
    { name: 'Monthly Donor', prompt: 'Thank a recurring monthly donor for their faithful giving' },
    { name: 'Large Gift', prompt: 'Thank someone for a significant donation' },
    { name: 'In-Kind Donation', prompt: 'Thank someone for donating items like food, clothes, or supplies' },
    { name: 'Year-End Thanks', prompt: 'Year-end thank you for all donations throughout the year' },
  ]
};

export default function AdminMarketing() {
  const [selectedTool, setSelectedTool] = useState('flyer');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('friendly');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [includeImage, setIncludeImage] = useState(true);
  
  // Letterhead specific
  const [letterheadData, setLetterheadData] = useState({
    volunteer_name: '',
    hours_worked: '',
    date_range: '',
    activities: '',
    custom_message: ''
  });
  
  // Welcome/Thank You note data
  const [noteData, setNoteData] = useState({
    recipient_name: '',
    donation_amount: '',
    donation_type: 'one-time',
    custom_details: ''
  });
  
  // Idea generator
  const [showIdeas, setShowIdeas] = useState(false);
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [ideas, setIdeas] = useState([]);
  
  // Share/Send modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    additionalMessage: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Share to social media
  const shareToSocial = (platform) => {
    const text = encodeURIComponent(generatedContent || '');
    const url = encodeURIComponent('https://livingfruitministry.org');
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?quote=${text}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${text}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      toast.success(`Opening ${platform}...`);
    }
  };
  
  // Send via email
  const sendEmail = async () => {
    if (!emailData.to) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/marketing/send-email`, {
        to: emailData.to,
        subject: emailData.subject || `${TOOLS.find(t => t.id === selectedTool)?.name} from Living Fruit Ministry`,
        content: generatedContent,
        additional_message: emailData.additionalMessage,
        content_type: selectedTool
      }, { withCredentials: true });
      
      toast.success('Email sent successfully!');
      setShowEmailModal(false);
      setEmailData({ to: '', subject: '', additionalMessage: '' });
    } catch (error) {
      console.error('Email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Use native share if available
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${TOOLS.find(t => t.id === selectedTool)?.name} - Living Fruit Ministry`,
          text: generatedContent,
          url: 'https://livingfruitministry.org'
        });
        toast.success('Shared successfully!');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      // Fallback to copy
      copyToClipboard();
    }
  };

  const handleGenerate = async () => {
    if (selectedTool === 'letterhead') {
      if (!letterheadData.volunteer_name || !letterheadData.hours_worked) {
        toast.error('Please fill in volunteer name and hours');
        return;
      }
      await generateLetterhead();
    } else if (selectedTool === 'welcome' || selectedTool === 'thankyou') {
      if (!noteData.recipient_name) {
        toast.error('Please enter the recipient name');
        return;
      }
      await generateNote();
    } else {
      if (!prompt.trim()) {
        toast.error('Please enter what you want to create');
        return;
      }
      await generateContent();
    }
  };
  
  const generateNote = async () => {
    setGenerating(true);
    setGeneratedContent(null);

    try {
      const notePrompt = selectedTool === 'welcome'
        ? `Write a heartfelt welcome note for ${noteData.recipient_name}${noteData.donation_amount ? ` who made a ${noteData.donation_type} donation of $${noteData.donation_amount}` : ''}. ${noteData.custom_details || ''}`
        : `Write a sincere thank you note for ${noteData.recipient_name} who donated${noteData.donation_amount ? ` $${noteData.donation_amount}` : ''}${noteData.donation_type ? ` (${noteData.donation_type})` : ''}. ${noteData.custom_details || ''}`;
      
      const textRes = await axios.post(`${API}/marketing/generate-text`, {
        prompt: notePrompt,
        content_type: selectedTool,
        tone: 'heartfelt'
      }, { withCredentials: true });
      
      setGeneratedContent(textRes.data.content);
      toast.success(`${selectedTool === 'welcome' ? 'Welcome' : 'Thank you'} note created!`);
    } catch (error) {
      console.error('Note generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate note');
    } finally {
      setGenerating(false);
    }
  };
  
  const generateIdeas = async () => {
    setGeneratingIdeas(true);
    setShowIdeas(true);
    
    try {
      const ideaPrompts = {
        flyer: "Give me 5 creative flyer ideas for a church ministry. Include event types, themes, and catchy headlines.",
        post: "Give me 5 engaging social media post ideas for a church ministry. Include different content types and hooks.",
        letterhead: "Give me 5 types of official letters a church ministry might need to send.",
        welcome: "Give me 5 creative ways to welcome new donors or members to a church ministry.",
        thankyou: "Give me 5 heartfelt thank you note ideas for church donors with different giving situations."
      };
      
      const res = await axios.post(`${API}/marketing/generate-text`, {
        prompt: ideaPrompts[selectedTool] + " Keep each idea brief (1-2 sentences). Format as a numbered list.",
        content_type: 'post',
        tone: 'helpful'
      }, { withCredentials: true });
      
      // Parse the response into individual ideas
      const ideaText = res.data.content;
      const parsedIdeas = ideaText.split(/\d+\.\s+/).filter(idea => idea.trim()).map(idea => idea.trim());
      setIdeas(parsedIdeas);
    } catch (error) {
      console.error('Ideas generation error:', error);
      toast.error('Failed to generate ideas');
      setIdeas(['Food pantry distribution announcement', 'Weekly worship service invite', 'Volunteer appreciation event', 'Community outreach day', 'Holiday celebration']);
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const generateContent = async () => {
    setGenerating(true);
    setGeneratedContent(null);
    setGeneratedImage(null);

    try {
      // Generate text first
      const textRes = await axios.post(`${API}/marketing/generate-text`, {
        prompt,
        content_type: selectedTool,
        tone
      }, { withCredentials: true });
      
      setGeneratedContent(textRes.data.content);
      
      // Generate image if requested
      if (includeImage) {
        try {
          const imageRes = await axios.post(`${API}/marketing/generate-image`, {
            prompt,
            content_type: selectedTool,
            style: tone,
            include_image: true
          }, { withCredentials: true });
          
          setGeneratedImage(imageRes.data.image_full);
        } catch (imgErr) {
          console.error('Image generation failed:', imgErr);
          toast.error('Text created, but image generation failed');
        }
      }
      
      toast.success('Content generated!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const generateLetterhead = async () => {
    setGenerating(true);
    setGeneratedContent(null);

    try {
      const res = await axios.post(`${API}/marketing/generate-letterhead`, {
        volunteer_name: letterheadData.volunteer_name,
        hours_worked: parseFloat(letterheadData.hours_worked),
        date_range: letterheadData.date_range,
        activities: letterheadData.activities,
        custom_message: letterheadData.custom_message
      }, { withCredentials: true });
      
      setGeneratedContent(res.data.content);
      toast.success('Letterhead created!');
    } catch (error) {
      console.error('Letterhead error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate letterhead');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedContent) {
      navigator.clipboard.writeText(generatedContent);
      toast.success('Copied to clipboard!');
    }
  };

  const downloadImage = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${generatedImage}`;
      link.download = `${selectedTool}_${Date.now()}.png`;
      link.click();
      toast.success('Image downloaded!');
    }
  };

  const printContent = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Living Fruit Ministry - ${selectedTool}</title>
          <style>
            body { font-family: Georgia, serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #228B22; padding-bottom: 20px; }
            .header h1 { color: #228B22; margin: 0; }
            .header p { color: #666; margin: 5px 0; }
            .content { white-space: pre-wrap; line-height: 1.8; }
            .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Living Fruit Ministry</h1>
            <p>109 N Beaumont Avenue #1078, Kissimmee, FL 34741</p>
            <p>(407) 216-2853 | livingfruitministry@gmail.com</p>
          </div>
          <div class="content">${generatedContent}</div>
          <div class="footer">
            <p>Living Fruit Ministry - Bearing Fruit in Our Community</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const applyTemplate = (template) => {
    setPrompt(template.prompt);
  };

  return (
    <div className="flex gap-6" data-testid="admin-marketing">
      {/* Main Content Area */}
      <div className="flex-1">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-charcoal">Marketing Studio</h1>
          <p className="text-charcoal/60 mt-1">Create flyers, posts, and letterheads with AI</p>
        </div>

        {/* Tool Selection */}
        <div className="grid grid-cols-5 gap-3 mb-8">
          {TOOLS.map((tool) => (
            <button
              key={tool.id}
              onClick={() => {
                setSelectedTool(tool.id);
                setGeneratedContent(null);
                setGeneratedImage(null);
                setShowIdeas(false);
              }}
              className={`p-3 rounded-2xl border-2 transition-all text-left ${
                selectedTool === tool.id 
                  ? 'border-primary bg-primary/5' 
                  : 'border-gold/10 hover:border-gold/30 bg-white'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl ${tool.color} flex items-center justify-center mb-2`}>
                <tool.icon size={18} />
              </div>
              <h3 className="font-semibold text-charcoal text-sm">{tool.name}</h3>
              <p className="text-xs text-charcoal/60 mt-1 line-clamp-2">{tool.description}</p>
            </button>
          ))}
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-2xl border border-gold/10 p-6 mb-6">
          {selectedTool === 'letterhead' ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal flex items-center gap-2">
                <FileText size={18} />
                Volunteer Hours Letterhead
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Volunteer Name *</Label>
                  <Input
                    value={letterheadData.volunteer_name}
                    onChange={(e) => setLetterheadData({...letterheadData, volunteer_name: e.target.value})}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Hours Worked *</Label>
                  <Input
                    type="number"
                    value={letterheadData.hours_worked}
                    onChange={(e) => setLetterheadData({...letterheadData, hours_worked: e.target.value})}
                    placeholder="20"
                  />
                </div>
              </div>
              
              <div>
                <Label>Date Range</Label>
                <Input
                  value={letterheadData.date_range}
                  onChange={(e) => setLetterheadData({...letterheadData, date_range: e.target.value})}
                  placeholder="January 2025 - March 2025"
                />
              </div>
              
              <div>
                <Label>Activities/Tasks</Label>
                <Input
                  value={letterheadData.activities}
                  onChange={(e) => setLetterheadData({...letterheadData, activities: e.target.value})}
                  placeholder="Food pantry distribution, community outreach..."
                />
              </div>
              
              <div>
                <Label>Custom Message (Optional)</Label>
                <Textarea
                  value={letterheadData.custom_message}
                  onChange={(e) => setLetterheadData({...letterheadData, custom_message: e.target.value})}
                  placeholder="Any additional message to include..."
                  rows={3}
                />
              </div>
            </div>
          ) : (selectedTool === 'welcome' || selectedTool === 'thankyou') ? (
            <div className="space-y-4">
              <h3 className="font-semibold text-charcoal flex items-center gap-2">
                {selectedTool === 'welcome' ? <UserPlus size={18} /> : <Heart size={18} />}
                {selectedTool === 'welcome' ? 'Welcome Note' : 'Thank You Note'}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Recipient Name *</Label>
                  <Input
                    value={noteData.recipient_name}
                    onChange={(e) => setNoteData({...noteData, recipient_name: e.target.value})}
                    placeholder="John & Mary Smith"
                  />
                </div>
                <div>
                  <Label>Donation Amount (Optional)</Label>
                  <Input
                    type="number"
                    value={noteData.donation_amount}
                    onChange={(e) => setNoteData({...noteData, donation_amount: e.target.value})}
                    placeholder="100"
                  />
                </div>
              </div>
              
              <div>
                <Label>Type</Label>
                <select
                  value={noteData.donation_type}
                  onChange={(e) => setNoteData({...noteData, donation_type: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="one-time">One-time Donation</option>
                  <option value="monthly">Monthly Giver (Fruit Bearer)</option>
                  <option value="in-kind">In-Kind Donation (Items)</option>
                  <option value="new-member">New Member</option>
                  <option value="new-volunteer">New Volunteer</option>
                  <option value="event-visitor">Event Visitor</option>
                </select>
              </div>
              
              <div>
                <Label>Additional Details (Optional)</Label>
                <Textarea
                  value={noteData.custom_details}
                  onChange={(e) => setNoteData({...noteData, custom_details: e.target.value})}
                  placeholder="Any specific details to mention... (e.g., 'donated canned goods for food pantry', 'visited our Sunday service')"
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-gold" />
                <span className="font-semibold text-charcoal">What would you like to create?</span>
              </div>
              
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`Describe what you want to create...

Example: "Create a flyer for our Saturday food pantry event. Include that we're giving away free groceries, fresh produce, and household items. Event is from 9am-12pm."`}
                rows={5}
                className="resize-none"
              />
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Tone:</Label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                  >
                    <option value="friendly">Friendly</option>
                    <option value="professional">Professional</option>
                    <option value="inspirational">Inspirational</option>
                    <option value="urgent">Urgent</option>
                    <option value="casual">Casual</option>
                  </select>
                </div>
                
                {(selectedTool === 'flyer' || selectedTool === 'post') && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeImage}
                      onChange={(e) => setIncludeImage(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm text-charcoal/70">Generate image</span>
                  </label>
                )}
              </div>
            </div>
          )}
          
          {/* Buttons Row */}
          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 bg-primary hover:bg-primary/90"
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2" size={18} />
                  Generate {TOOLS.find(t => t.id === selectedTool)?.name}
                </>
              )}
            </Button>
            
            <Button
              onClick={generateIdeas}
              disabled={generatingIdeas}
              variant="outline"
              size="lg"
              className="gap-2"
            >
              {generatingIdeas ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Lightbulb size={18} />
              )}
              Need Ideas?
            </Button>
          </div>
          
          {/* Ideas Panel */}
          <AnimatePresence>
            {showIdeas && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                    <Lightbulb size={16} />
                    Ideas for your {TOOLS.find(t => t.id === selectedTool)?.name}
                  </h4>
                  <button onClick={() => setShowIdeas(false)} className="text-amber-600 hover:text-amber-800">
                    <X size={16} />
                  </button>
                </div>
                {generatingIdeas ? (
                  <div className="flex items-center gap-2 text-amber-700">
                    <Loader2 className="animate-spin" size={16} />
                    Generating ideas...
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ideas.map((idea, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setPrompt(idea);
                          setShowIdeas(false);
                        }}
                        className="w-full text-left p-2 rounded-lg bg-white hover:bg-amber-100 text-sm text-charcoal/80 transition-colors"
                      >
                        {idea}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Generated Content */}
        <AnimatePresence>
          {generatedContent && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-2xl border border-gold/10 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-charcoal flex items-center gap-2">
                  <CheckCircle size={18} className="text-green-500" />
                  Generated Content
                </h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyToClipboard}>
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                  <Button variant="outline" size="sm" onClick={printContent}>
                    <Printer size={14} className="mr-1" /> Print
                  </Button>
                  {generatedImage && (
                    <Button variant="outline" size="sm" onClick={downloadImage}>
                      <Download size={14} className="mr-1" /> Image
                    </Button>
                  )}
                </div>
              </div>
              
              {generatedImage && (
                <div className="mb-4 rounded-xl overflow-hidden border border-gold/10">
                  <img 
                    src={`data:image/png;base64,${generatedImage}`} 
                    alt="Generated marketing image"
                    className="w-full max-h-96 object-contain bg-gray-50"
                  />
                </div>
              )}
              
              <div className="bg-cream rounded-xl p-4 whitespace-pre-wrap text-charcoal/80 leading-relaxed mb-4">
                {generatedContent}
              </div>
              
              {/* Share & Send Section */}
              <div className="border-t border-gold/10 pt-4">
                <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
                  <Share2 size={16} />
                  Share & Send
                </h4>
                
                <div className="flex flex-wrap gap-2">
                  {/* Social Media Buttons */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => shareToSocial('facebook')}
                    className="gap-1 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
                  >
                    <Facebook size={14} /> Facebook
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => shareToSocial('twitter')}
                    className="gap-1 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-300"
                  >
                    <Twitter size={14} /> X/Twitter
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => shareToSocial('whatsapp')}
                    className="gap-1 hover:bg-green-50 hover:text-green-600 hover:border-green-300"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => shareToSocial('linkedin')}
                    className="gap-1 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300"
                  >
                    <Linkedin size={14} /> LinkedIn
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowEmailModal(true)}
                    className="gap-1 hover:bg-purple-50 hover:text-purple-600 hover:border-purple-300"
                  >
                    <Mail size={14} /> Email
                  </Button>
                  
                  {navigator.share && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={nativeShare}
                      className="gap-1 hover:bg-gray-100"
                    >
                      <Share2 size={14} /> More...
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={18} />
              Send via Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({...emailData, to: e.target.value})}
                placeholder="recipient@email.com"
              />
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                placeholder={`${TOOLS.find(t => t.id === selectedTool)?.name} from Living Fruit Ministry`}
              />
            </div>
            
            <div>
              <Label>Additional Message (Optional)</Label>
              <Textarea
                value={emailData.additionalMessage}
                onChange={(e) => setEmailData({...emailData, additionalMessage: e.target.value})}
                placeholder="Add a personal note..."
                rows={3}
              />
            </div>
            
            <div className="bg-cream rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-xs text-charcoal/50 mb-1">Content to be sent:</p>
              <p className="text-sm text-charcoal/70 whitespace-pre-wrap line-clamp-4">
                {generatedContent}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendEmail} 
              disabled={sendingEmail}
              className="bg-primary hover:bg-primary/90"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toolbox Sidebar */}
      <div className="w-80 shrink-0">
        <div className="bg-white rounded-2xl border border-gold/10 p-5 sticky top-4">
          <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Palette size={18} />
            Quick Templates
          </h3>
          
          <div className="space-y-2">
            {QUICK_TEMPLATES[selectedTool]?.map((template, i) => (
              <button
                key={i}
                onClick={() => applyTemplate(template)}
                className="w-full text-left p-3 rounded-xl bg-cream hover:bg-gold/10 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-charcoal">{template.name}</span>
                  <ChevronRight size={14} className="text-charcoal/30 group-hover:text-charcoal/60" />
                </div>
              </button>
            ))}
          </div>
          
          <hr className="my-5 border-gold/10" />
          
          <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
            <MessageSquare size={18} />
            Tips
          </h3>
          
          <div className="space-y-3 text-sm text-charcoal/70">
            <p>• Be specific about what you want</p>
            <p>• Include dates, times, and locations</p>
            <p>• Mention the target audience</p>
            <p>• Add any special details or themes</p>
          </div>
          
          <hr className="my-5 border-gold/10" />
          
          <div className="text-center">
            <p className="text-xs text-charcoal/50 mb-2">Ministry Branding</p>
            <div className="flex justify-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#228B22]" title="Primary Green" />
              <div className="w-8 h-8 rounded-full bg-[#F0FFF0] border" title="Light Green" />
              <div className="w-8 h-8 rounded-full bg-[#1E3A5F]" title="Navy" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
